import React, { useState, useCallback, useEffect } from "react";
import useStyles from "./style";
import {
  Button,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
} from "@mui/material";
import {
  EditOutlined,
  DeleteOutline,
  AddOutlined,
  SearchOutlined,
  ChevronLeft,
  ChevronRight,
} from "@mui/icons-material";
import * as XLSX from "xlsx";
import Papa from "papaparse";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { showLoader, hideLoader } from "Redux/Loader/actions";
import { showSnackbar } from "Redux/SnackBar/actions";
import { useSelector, useDispatch } from "react-redux";
import _ from "lodash";

// Services
import AppIconapi from "Services/AppIcon";

// Modals
import AppIconCreateModal from "Pages/Modals/AppIcon/Create";
import AppIconUpdateModal from "Pages/Modals/AppIcon/Update";
import AppIconDeleteModal from "Pages/Modals/AppIcon/Delete";

const AppHomeIcons = () => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const demoaccount_id = useSelector(
    (state) => state?.AuthReducer?.demoaccount_id || ""
  );

  const [row, setRow] = useState([]);
  const [bulkrow, setbulkrow] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [searchText, setSearchText] = useState("");

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);

  // Modals
  const [addModal, setAddModal] = useState(false);
  const [updateModal, setUpdateModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);

  const handleOpenAddModal = () => {
    setAddModal(true);
  };

  const handleCloseAddModal = () => {
    setAddModal(false);
    getIcons();
  };

  const handleOpenUpdateModal = (rowItem) => {
    setSelectedId(rowItem?._id);
    setUpdateModal(true);
  };

  const handleCloseUpdateModal = () => {
    setUpdateModal(false);
    getIcons();
  };

  const handleOpenDeleteModal = (rowItem) => {
    setSelectedId(rowItem?._id);
    setDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setDeleteModal(false);
    getIcons();
  };

  // Get App Icons list
  const getIcons = useCallback(() => {
    dispatch(showLoader("Loading please wait..."));
    const onSuccess = (res) => {
      dispatch(hideLoader());
      if (res?.data?.status === true) {
        setTotalPages(res?.data?.data?.totalPages || 1);
        setRow(res?.data?.data?.data || []);
      } else {
        setRow([]);
      }
    };
    const onFailure = (err) => {
      dispatch(hideLoader());
      dispatch(
        showSnackbar({
          message: err?.response?.data?.message || "Failed to fetch icons data",
          autoHideDuration: 3000,
          anchorOrigin: { vertical: "top", horizontal: "right" },
          variant: "error",
        })
      );
    };
    const params = { page, limit: pageSize, search: searchText };
    if (demoaccount_id) {
      params.demoaccount_id = demoaccount_id;
    }
    AppIconapi.getAppIconList(params).then(onSuccess, onFailure);
  }, [page, pageSize, searchText, demoaccount_id, dispatch]);

  const getAllIcons = useCallback(() => {
    const onSuccess = (res) => {
      if (res?.data?.status === true) {
        setbulkrow(res?.data?.data?.data || []);
      } else {
        setbulkrow([]);
      }
    };
    const onFailure = () => {};
    const params = { page: 1, limit: "unlimited", search: "" };
    if (demoaccount_id) {
      params.demoaccount_id = demoaccount_id;
    }
    AppIconapi.getAppIconList(params).then(onSuccess, onFailure);
  }, [demoaccount_id]);

  useEffect(() => {
    getIcons();
    getAllIcons();
  }, [getIcons, getAllIcons]);

  const calculateSno = (pageIndex, pSize, index) => {
    return (pageIndex - 1) * pSize + index + 1;
  };

  const dataRows = _.map(row || [], (data, index) => ({
    sno: calculateSno(page, pageSize, index),
    _id: data?._id,
    name: data?.name || "-",
    icon: data?.icon || "",
    serviceTypes: data?.serviceTypes || [],
    status: data?.status !== undefined ? data?.status : 1,
    row: data,
  }));

  const bulkDataRows = _.map(bulkrow || [], (data, index) => ({
    "S.No": index + 1,
    "Icon Name": data?.name || "-",
    "Service Types":
      (data?.serviceTypes || [])
        .map((s) => s?.serviceName || s?.name || s)
        .join(", ") || "-",
    "Icon URL": data?.icon || "-",
    "Status": data?.status === 1 ? "Active" : "Inactive",
  }));

  // CSV Export
  const downloadCSV = () => {
    if (!bulkDataRows.length) return;
    const csv = Papa.unparse(bulkDataRows);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", "App_Icons_Report.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Excel Export
  const downloadExcel = () => {
    if (!bulkDataRows.length) return;
    const worksheet = XLSX.utils.json_to_sheet(bulkDataRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "App Icons");
    XLSX.writeFile(workbook, "App_Icons_Report.xlsx");
  };

  // PDF Export
  const downloadPDF = () => {
    if (!bulkDataRows.length) return;
    const doc = new jsPDF();
    doc.text("App Icons List", 14, 15);
    const tableColumn = ["S.No", "Icon Name", "Service Types", "Icon URL", "Status"];
    const tableRows = bulkDataRows.map((rowItem) => [
      rowItem["S.No"],
      rowItem["Icon Name"],
      rowItem["Service Types"],
      rowItem["Icon URL"],
      rowItem["Status"],
    ]);

    doc.autoTable({
      head: [tableColumn],
      body: tableRows,
      startY: 20,
    });
    doc.save("App_Icons_Report.pdf");
  };

  return (
    <Box className={classes.root}>
      {/* Header */}
      <Box className={classes.headerContainer}>
        <Typography className={classes.pageTitle}>App Home Icons</Typography>
        <Typography className={classes.breadcrumb}>
          App Home Screen Setup / <span>App Icons</span>
        </Typography>
      </Box>

      {/* Action Row */}
      <Box className={classes.actionRow}>
        <Box className={classes.searchWrapper}>
          <SearchOutlined className={classes.searchIcon} />
          <input
            type="text"
            placeholder="Search Icon Name..."
            className={classes.searchInput}
            value={searchText}
            onChange={(e) => {
              setSearchText(e.target.value);
              setPage(1);
            }}
          />
        </Box>

        <Box className={classes.rightActions}>
          <Box className={classes.exportGroup}>
            <Button className={classes.exportButton} onClick={downloadCSV}>
              CSV
            </Button>
            <Button className={classes.exportButton} onClick={downloadExcel}>
              Excel
            </Button>
            <Button className={classes.exportButton} onClick={downloadPDF}>
              PDF
            </Button>
          </Box>

          {!demoaccount_id && (
            <Button
              className={classes.addButton}
              startIcon={<AddOutlined />}
              onClick={handleOpenAddModal}
            >
              Add New Icon
            </Button>
          )}
        </Box>
      </Box>

      {/* Table */}
      <TableContainer component={Paper} elevation={0} className={classes.tableWrapper}>
        <Table>
          <TableHead>
            <TableRow className={classes.tableHeaderRow}>
              <TableCell className={classes.tableHeaderCell}>S.No</TableCell>
              <TableCell className={classes.tableHeaderCell}>Icon Image</TableCell>
              <TableCell className={classes.tableHeaderCell}>Icon Name</TableCell>
              <TableCell className={classes.tableHeaderCell}>Service Types</TableCell>
              <TableCell className={classes.tableHeaderCell}>Status</TableCell>
              {!demoaccount_id && (
                <TableCell className={classes.tableHeaderCell} align="right">Actions</TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {dataRows.map((rowItem) => (
              <TableRow key={rowItem._id} className={classes.tableRow}>
                <TableCell className={classes.tableCell}>{rowItem.sno}</TableCell>
                <TableCell className={classes.tableCell}>
                  {rowItem.icon ? (
                    <Box
                      component="img"
                      src={rowItem.icon}
                      alt={rowItem.name}
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "8px",
                        objectFit: "cover",
                        border: "1px solid #e2e8f0",
                        boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                      }}
                    />
                  ) : (
                    "-"
                  )}
                </TableCell>
                <TableCell className={`${classes.tableCell} ${classes.boldText}`}>
                  {rowItem.name}
                </TableCell>
                <TableCell className={classes.tableCell}>
                  {rowItem.serviceTypes && rowItem.serviceTypes.length > 0 ? (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, maxWidth: 220 }}>
                      {rowItem.serviceTypes.map((st) => (
                        <Chip
                          key={st?._id || st}
                          label={st?.serviceName || st?.name || st}
                          size="small"
                          sx={{
                            backgroundColor: "#f1f5f9",
                            color: "#334155",
                            fontWeight: 500,
                            fontSize: "12px",
                            borderRadius: "6px",
                          }}
                        />
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="body2" sx={{ color: "#9ca3af" }}>-</Typography>
                  )}
                </TableCell>
                <TableCell className={classes.tableCell}>
                  <Chip
                    label={rowItem.status === 1 ? "Active" : "Inactive"}
                    size="small"
                    sx={{
                      backgroundColor: rowItem.status === 1 ? "#e6f4ea" : "#fce8e6",
                      color: rowItem.status === 1 ? "#137333" : "#c5221f",
                      fontWeight: 600,
                      borderRadius: "6px",
                    }}
                  />
                </TableCell>
                {!demoaccount_id && (
                  <TableCell className={classes.tableCell} align="right">
                    <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                      <Box
                        className={classes.actionButton}
                        onClick={() => handleOpenUpdateModal(rowItem)}
                      >
                        <EditOutlined fontSize="small" /> Edit
                      </Box>
                      <Box
                        className={classes.actionDeleteButton}
                        onClick={() => handleOpenDeleteModal(rowItem)}
                      >
                        <DeleteOutline fontSize="small" /> Delete
                      </Box>
                    </Box>
                  </TableCell>
                )}
              </TableRow>
            ))}
            {dataRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={demoaccount_id ? 4 : 5} style={{ textAlign: "center", padding: "40px" }}>
                  <Typography variant="body2" color="textSecondary">
                    No app icons found
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        {/* Pagination */}
        <Box className={classes.paginationContainer}>
          <Typography className={classes.paginationText}>
            Page {page} of {totalPages}
          </Typography>
          <Box className={classes.paginationControls}>
            <ChevronLeft
              className={`${classes.pageArrow} ${page === 1 ? "disabled" : ""}`}
              onClick={() => page > 1 && setPage(page - 1)}
            />
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
              .map((p, index, array) => {
                const prev = array[index - 1];
                const showDots = prev && p - prev > 1;
                return (
                  <React.Fragment key={p}>
                    {showDots && <span className={classes.pageDots}>...</span>}
                    <Button
                      className={p === page ? classes.pageButtonActive : classes.pageButton}
                      onClick={() => setPage(p)}
                    >
                      {p}
                    </Button>
                  </React.Fragment>
                );
              })}
            <ChevronRight
              className={`${classes.pageArrow} ${page === totalPages ? "disabled" : ""}`}
              onClick={() => page < totalPages && setPage(page + 1)}
            />
          </Box>
        </Box>
      </TableContainer>

      {/* Create Modal */}
      {addModal && (
        <AppIconCreateModal
          openDialog={addModal}
          handleClose={handleCloseAddModal}
        />
      )}

      {/* Update Modal */}
      {updateModal && (
        <AppIconUpdateModal
          openDialog={updateModal}
          id={selectedId}
          handleClose={handleCloseUpdateModal}
        />
      )}

      {/* Delete Modal */}
      {deleteModal && (
        <AppIconDeleteModal
          openDialog={deleteModal}
          id={selectedId}
          handleClose={handleCloseDeleteModal}
        />
      )}
    </Box>
  );
};

export default AppHomeIcons;
